/**
 * GD Video Player — custom controls, play overlay, and fade-out behavior.
 * Loaded as an external asset to avoid inline <script> rendering issues
 * inside overflow:hidden containers.
 */
(function() {
  function initGdVideoPlayer(container) {
    var videoPlayer = container.querySelector('video');
    var playButton = container.querySelector('.gd-video__play-button');
    var muteBtn = container.querySelector('.gd-video__mute-btn');
    var timeline = container.querySelector('.gd-video__timeline');
    var controls = container.querySelector('.gd-video__controls');

    if (!videoPlayer) return null;

    var isAutoplay = videoPlayer.hasAttribute('autoplay');
    var fadeOutTimeout;

    /* --- Play / Pause button visibility sync --- */
    if (playButton) {
      videoPlayer.addEventListener('play', function() {
        playButton.classList.add('gd-video__play-button--hidden');
      });

      videoPlayer.addEventListener('pause', function() {
        playButton.classList.remove('gd-video__play-button--hidden');
      });

      videoPlayer.addEventListener('ended', function() {
        playButton.classList.remove('gd-video__play-button--hidden');
      });
    }

    /* --- Click-to-toggle (wrapper-level delegation) ---
       Binding on the wrapper catches clicks regardless of positioning
       context (e.g. when Dawn's .media > * rule absolutely positions
       the wrapper and the <video> sits inside). Excludes clicks on
       control elements so they keep their own behavior.
    */
    function togglePlay(e) {
      if (isAutoplay) return;
      if (e && e.target && e.target.closest) {
        if (
          e.target.closest('.gd-video__controls') ||
          e.target.closest('.gd-video__mute-btn') ||
          e.target.closest('.gd-video__timeline')
        ) {
          return;
        }
      }
      if (videoPlayer.paused || videoPlayer.ended) {
        var p = videoPlayer.play();
        if (p && typeof p.catch === 'function') p.catch(function() {});
      } else {
        videoPlayer.pause();
      }
    }

    container.addEventListener('click', togglePlay);

    /* --- Mute / Unmute --- */
    if (muteBtn) {
      muteBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        videoPlayer.muted = !videoPlayer.muted;
        if (videoPlayer.muted) {
          muteBtn.classList.remove('gd-video__mute-btn--unmuted');
          muteBtn.classList.add('gd-video__mute-btn--muted');
          muteBtn.setAttribute('aria-label', 'Unmute');
        } else {
          muteBtn.classList.remove('gd-video__mute-btn--muted');
          muteBtn.classList.add('gd-video__mute-btn--unmuted');
          muteBtn.setAttribute('aria-label', 'Mute');
        }
      });
    }

    /* --- Timeline --- */
    if (timeline) {
      videoPlayer.addEventListener('timeupdate', function() {
        if (videoPlayer.duration) {
          timeline.value = (videoPlayer.currentTime / videoPlayer.duration) * 100;
        }
      });

      timeline.addEventListener('input', function(e) {
        e.stopPropagation();
        if (videoPlayer.duration) {
          videoPlayer.currentTime = (timeline.value / 100) * videoPlayer.duration;
        }
      });

      timeline.addEventListener('click', function(e) {
        e.stopPropagation();
      });
    }

    /* --- Fade-out controls (2s inactivity) --- */
    if (controls) {
      function startFadeOutTimer() {
        clearTimeout(fadeOutTimeout);
        fadeOutTimeout = setTimeout(function() {
          controls.classList.add('gd-video__controls--fade-out');
        }, 2000);
      }

      function stopFadeOutTimer() {
        controls.classList.remove('gd-video__controls--fade-out');
        clearTimeout(fadeOutTimeout);
      }

      container.addEventListener('mousemove', stopFadeOutTimer);
      container.addEventListener('mousemove', function() { startFadeOutTimer(); });
      container.addEventListener('mouseleave', function() { startFadeOutTimer(); });
      container.addEventListener('touchstart', stopFadeOutTimer);
      container.addEventListener('touchend', function() { startFadeOutTimer(); });

      startFadeOutTimer();
    }

    /* --- Programmatic autoplay fallback ---
       The autoplay attribute alone can fail silently in some browsers
       (especially on iOS / after theme editor reload). Forcing muted
       and calling play() once the media is ready maximizes the chance
       that autoplay actually kicks in. If the browser still blocks,
       we resume on the first user interaction.
    */
    if (isAutoplay) {
      var tryPlay = function() {
        try {
          videoPlayer.muted = true;
          var p = videoPlayer.play();
          if (p && typeof p.catch === 'function') {
            p.catch(function() {
              var resume = function() {
                videoPlayer.muted = true;
                videoPlayer.play().catch(function() {});
                document.removeEventListener('click', resume, true);
                document.removeEventListener('touchstart', resume, true);
                document.removeEventListener('scroll', resume, true);
              };
              document.addEventListener('click', resume, true);
              document.addEventListener('touchstart', resume, true);
              document.addEventListener('scroll', resume, true);
            });
          }
        } catch (err) { /* noop */ }
      };

      if (videoPlayer.readyState >= 2) {
        tryPlay();
      } else {
        videoPlayer.addEventListener('loadeddata', tryPlay, { once: true });
        videoPlayer.addEventListener('canplay', tryPlay, { once: true });
      }
    }

    return videoPlayer;
  }

  /* Expose globally so sections can call it (e.g. after Splide remount) */
  window.initGdVideoPlayer = initGdVideoPlayer;

  /** Init all uninitialized gd-video wrappers */
  function initAllGdVideos() {
    var containers = document.querySelectorAll('.gd-video-wrapper:not([data-gd-video-initialized])');
    containers.forEach(function(container) {
      initGdVideoPlayer(container);
      container.dataset.gdVideoInitialized = 'true';
    });
  }

  /* Auto-init on DOMContentLoaded */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGdVideos);
  } else {
    initAllGdVideos();
  }

  /* Shopify design mode: re-init on section load */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initAllGdVideos);
  }

  /* --- Resume autoplay background videos after a modal closes ---
     Dawn's ModalDialog.hide() dispatches 'modalClosed' and then calls
     pauseAllMedia(), which pauses every <video> on the page (including our
     autoplay background video) without ever resuming it. We re-play autoplay
     gd-videos on the next tick, i.e. after that pauseAllMedia() has run. Only
     autoplay videos are touched, so user-controlled videos stay as they are.
  */
  document.body.addEventListener('modalClosed', function () {
    setTimeout(function () {
      document.querySelectorAll('.gd-video-wrapper video[autoplay]').forEach(function (v) {
        if (!v.paused) return;
        v.muted = true;
        var p = v.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      });
    }, 0);
  });
})();
